import { cloneDeep } from "lodash";
import { useEffect, useRef, useState } from "react";

import {
  createFeatureResourceToast,
  createScreenshareRequestToast,
} from "components/shared/toasts";
import { useCapability, useUserIdsWithCapability } from "hooks/capabilities";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import {
  USER_PICKER_PROPERTIES,
  useUserProfileProperties,
  useUsersProperties,
} from "hooks/use_user_properties";
import {
  addUserToParticipantBarAndToBestPaneOnStage,
  featureContentInOneUp,
  setAllStagePanes,
} from "shared/reducers/classroom";
import {
  CapabilityType,
  MinervaResourceSubtypeType,
  PaneContentType,
  PaneType,
  ResourceTypeType,
} from "shared/types";
import { ResourceType } from "shared/types/classroom";
import { classroomSelectors, localSelectors } from "store";
import { isLeader } from "utils/user";

// Observes resources so toast notifications can be dispatched when a new resource is added
// Separate from the resource sidebar since the resource sidebar isn't always visible
// and receiving events
// Only used by users with the "can control classroom" capability
// Additionally handles adding previous panes back to the stage when a students screenshare is ended.
function ResourceObserver() {
  const dispatch = useAppDispatch();
  const firstRenderRef = useRef(true);
  const prevResourcesRef = useRef<Record<string, ResourceType>>();
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const activeBreakoutSessionId = useAppSelector(
    classroomSelectors.selectActiveBreakoutSessionId(),
  );
  const [resourcesToToastQueue, setResourcesToToastQueue] = useState<ResourceType[]>([]);
  const lastResource = resourcesToToastQueue[0] || null;
  const resources = useAppSelector(classroomSelectors.selectResources());
  const uploader = useUserProfileProperties(lastResource?.uploader_id || "");
  const uploaderCanControlClassroom = useCapability(CapabilityType.CAN_CONTROL_CLASSROOM, {
    // if userId is falsy, useCapability will return the current user's capability
    userId: uploader?.id || "-1",
  });
  const { panes } = useAppSelector(classroomSelectors.selectStage());

  // A map which holds screenshareId as key and previous panes as value.
  const [previousPanesByScreenshareId, setPreviousPanesByScreenshareId] = useState(
    new Map<string, PaneType[]>(),
  );
  const resourcesIds = Object.values(resources).map((resource) => resource.id);

  const users = Object.values(useUsersProperties(USER_PICKER_PROPERTIES));
  const isAlwaysSubscribedToUserIds = useUserIdsWithCapability(
    CapabilityType.IS_ALWAYS_SUBSCRIBED_TO,
  );
  const isCurrentUserTheLeader = isLeader(currentUserId, users, isAlwaysSubscribedToUserIds);

  // A recursive method which checks if the screenshare is ended by the student.
  // and dispatches the previous panes to the stage if so.
  const checkScreenshareEnded = (_panes: PaneType[], isFirstCall: boolean = false) => {
    const paneId = _panes && _panes.length === 1 && _panes[0].content_id;
    const isSavedScreenshare = paneId && previousPanesByScreenshareId.has(paneId);

    if (isFirstCall && !isSavedScreenshare) {
      if (previousPanesByScreenshareId.size > 0) setPreviousPanesByScreenshareId(new Map());
      return;
    }

    if (isSavedScreenshare && !resourcesIds.includes(paneId)) {
      // Pane exists but resource gone (Student stopped sharing)
      const previousPanes = previousPanesByScreenshareId.get(paneId);
      setPreviousPanesByScreenshareId((prev) => {
        const map = new Map(prev);
        map.delete(paneId);
        return map;
      });
      if (previousPanes) checkScreenshareEnded(previousPanes);
    } else if (!isFirstCall) {
      dispatch(setAllStagePanes({ panes: _panes }));
    }
  };

  // Leader watcher for screenshares that are ended by the student.
  useEffect(() => {
    if (!isCurrentUserTheLeader || activeBreakoutSessionId) return;

    checkScreenshareEnded(panes, true);

    // React to resources only when length changes (screenshare ended(resource deleted))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourcesIds.length]);

  useEffect(() => {
    const prevResources: Record<string, ResourceType> = { ...(prevResourcesRef.current || {}) };
    prevResourcesRef.current = resources;

    // Do not show toast on first render
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      return;
    }

    const newResources = Object.values(resources).filter((resource) => !prevResources[resource.id]);

    if (newResources.length > 0) {
      setResourcesToToastQueue((queue) => [...queue, ...newResources]);
    }
  }, [resources]);

  useEffect(() => {
    if (!lastResource) {
      return;
    }

    // Do not show toast if the last resource:
    // - has no uploader user
    // - was uploaded during a breakout
    // - was uploaded by someone who can control the classroom
    // - is a captions doc
    if (
      !uploader ||
      activeBreakoutSessionId ||
      uploaderCanControlClassroom ||
      lastResource.subtype === MinervaResourceSubtypeType.CaptionsDoc
    ) {
      // Skip this resource and move to the next one
      setResourcesToToastQueue((queue) => queue.slice(1));
      return;
    }

    const handleFeature = () => {
      if (lastResource.type === ResourceTypeType.Screenshare) {
        setPreviousPanesByScreenshareId((prev) =>
          new Map(prev).set(lastResource.id, cloneDeep(panes)),
        );
      }
      dispatch(
        featureContentInOneUp({
          contentId: lastResource.id,
          contentType: PaneContentType.Resource,
          ownerId: currentUserId,
        }),
      );
      dispatch(addUserToParticipantBarAndToBestPaneOnStage({ userId: uploader?.id }));
    };
    if (lastResource.type === ResourceTypeType.Screenshare) {
      if (!previousPanesByScreenshareId.has(lastResource.id))
        createScreenshareRequestToast(uploader, handleFeature);
    } else {
      createFeatureResourceToast(uploader, false, lastResource.title, handleFeature);
    }

    // Remove the processed resource from the queue
    setResourcesToToastQueue((queue) => queue.slice(1));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastResource?.id]);

  return null;
}

export { ResourceObserver };
